import { BaseService } from "./BaseService";
import { ITextBlockArray } from "Entities";
import { CoreContext, QueryParameter } from "Core";
import { Configuration } from "Configuration";
import { Util } from "Core/Util";

/**
 * Provides access to TextBlocks
 */
export class TextBlockService extends BaseService {

    private textblockPromise: Promise<{ [textBlockCategory: string]: ITextBlockArray }>;
    private missingTextBlocks: { [textBlockCategory: string]: ITextBlockArray };

    /**
     * Loads TextBlocks from server.
     * @param languageCode LanguageCode i.e. "E", "D".
     * @param category TextBlockCategory, usually the mappingName in lowercase.
     */
    public load(languageCode: string, category: string): Promise<{ [textBlockCategory: string]: ITextBlockArray }> {
        return this.textblockPromise = this.ajaxget(Configuration.baseUrl + "textblock/" + languageCode + "/" + category);
    }

    /**
     * Gets all last loaded TextBlocks, usually the TextBlocks of the loaded mappings.
     */
    public getAll(): Promise<{ [textBlockCategory: string]: ITextBlockArray }> {
        return this.textblockPromise;
    }

    /**
     * Gets a single TextBlock for the current mapping.
     * @param textBlockName Name of the TextBlock.    
     * @param textBlockCategory (Optional) Name of the textBlockCategory. If omitted, the currentTextBlockCategory is used.
     * @param returnEmpty (Optional) True if an empty string should be returned if the textblock is missing.
     */
    public get(textBlockName: string, textBlockCategory?: string, returnEmpty?: boolean): string {
        let textBlocks = CoreContext.Instance.pageViewModel.get("TextBlock");
        if (textBlockCategory) {
            return this.getChecked(textBlocks[textBlockCategory], textBlockName, textBlockCategory, returnEmpty);
        } else {
            return this.getChecked(textBlocks[CoreContext.Instance.currentTextBlockCategory], textBlockName, CoreContext.Instance.currentTextBlockCategory, returnEmpty);
        }
    }

    /**
     * Gets the TextBlock from the given ITextBlockArray with checks if the TextBlock exists.
     * Resolves the TextBlock if it contains a script.
     * @param textBlocks List of current TextBlocks.
     * @param textBlockName Name of TextBlock.
     * @param returnEmpty (Optional) True if an empty string should be returned if the textblock is missing.     
     */
    public getChecked(textBlocks: ITextBlockArray, textBlockName: string, textBlockCategory?: string, returnEmpty?: boolean): string {
        if (!textBlockName) {
            return "";
        }
        if (textBlocks == null) {
            return "";
        }

        if (textBlockName.substr(0, 6) === "\\\\#\\\\#") {
            return textBlockName.substr(6);
        }

        if (!textBlocks.hasOwnProperty(textBlockName)) {
            // add missing TextBlock
            if (!this.missingTextBlocks) {
                this.missingTextBlocks = {};
            }
            if (!this.missingTextBlocks[textBlockCategory]) {
                this.missingTextBlocks[textBlockCategory] = {};
            }
            this.missingTextBlocks[textBlockCategory][textBlockName] = "";
            if (returnEmpty) {
                return "";
            } else {
                return `<span class='dms-missing-textblock'>${textBlockName}</span>`;
            }
        }
        let textBlock = textBlocks[textBlockName];
        if (textBlock == null) {
            if (returnEmpty) {
                return "";
            } else {
                return `<span class='dms-missing-textblock'>${textBlockName}</span>`;                
            }
        }
        if (textBlock && textBlock.indexOf("#") >= 0) {

            textBlock = Util.resolve(textBlock, {});
        }
        return textBlock;
    }

    /**
     * Uploads the missing TextBlocks which are stored in missingTextBlocks
     */
    public uploadMissingTextBlocks(): void {
        if (this.missingTextBlocks) {
            let autogenerateTextBlockParam = (QueryParameter.get("autogeneratetb") || "").toLowerCase();
            let autogenerateTextBlock = Util.parseBoolean(autogenerateTextBlockParam);
            this.post(`${Configuration.baseUrl}textblock/set${(autogenerateTextBlock ? "?autogeneratetb=true" : "")}`, this.missingTextBlocks);
        }
    }
}